import { useState } from "react";
import { t } from "i18next";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import { BOOKING_CONSTANTS, TIME_CONSTANTS } from "@constants/Bookings";
import {
  EventBackgroundType,
  EventSubType,
  EventType,
} from "@enums/component.enum";
import { DateFormats } from "@enums/shared.enum";
import { EventContentArg } from "@fullcalendar/core";
import { useBooking } from "@hooks/useBooking/useBooking";
import {
  BookingCalendarApi,
  BookingCalendarEvent,
  BookingCalendarResources,
  BookingEventApi,
  BookingFetchResponseType,
  BookingSlotApi,
  ResourceType,
} from "@models/services/booking";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import bookingCalendarSlice from "@slices/bookingCalendar.slice";
import { RootState } from "@store/index";
export const useBookingCalendar = (slots: BookingFetchResponseType) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [resources, setResources] = useState<BookingCalendarResources[]>([]);
  const [calendarTime, setCalendarTime] = useState<{
    start: string;
    end: string;
  }>({
    end: TIME_CONSTANTS.END_OF_DAY,
    start: TIME_CONSTANTS.START_OF_DAY,
  });

  const { getNextTimeMark, getSelectedResource } = useBooking();
  const dispatch = useDispatch();

  const selectedDate = useSelector(
    (State: RootState) => State.booking.selectedDate
  );

  const fetchCalendarData = async ({ locationId }: BookingSlotApi) => {
    if (!locationId && !slots?.showCalendar) {
      return;
    }
    setIsLoading(true);
    const data = await API_SERVICES.BOOKING.GET_BOOKING_EVENTS({
      bookingDate: selectedDate,
      locationId,
    })
      .then(response => {
        const responseData = handleResponse(response);
        if (responseData?.ok) {
          updateCalendarEvents(response.data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
    return data;
  };

  const fetchCalendarParticipant = async (bookingId: string) => {
    setIsLoading(true);
    const data = await API_SERVICES.BOOKING.GET_BOOKING_EVENTS_PARTICIPANT(
      bookingId
    )
      .then(response => {
        const responseData = handleResponse(response);
        if (responseData?.ok) {
          setIsLoading(false);
          return response.data;
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
    return data;
  };

  const updateCalendarEvents = (response: BookingCalendarApi) => {
    if (!response) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const hasQuotaExhausted = response.events.some(
      (event: BookingEventApi) => event.subType === EventSubType.QuotaExhausted
    );

    const events: BookingCalendarEvent[] = response.events
      .filter(
        event =>
          event !== null &&
          !(hasQuotaExhausted && event.subType === EventSubType.AdvanceBooking)
      )
      .map((event: BookingEventApi) => {
        let start = event.start;
        let end = event.end;

        if (moment(start, DateFormats.YMDTimeWithSeconds, true).isValid()) {
          start = moment(start, DateFormats.YMDTimeWithSeconds).format(
            DateFormats.YMDTime
          );
        }

        if (moment(end, DateFormats.YMDTimeWithSeconds, true).isValid()) {
          end = moment(end, DateFormats.YMDTimeWithSeconds).format(
            DateFormats.YMDTime
          );
        }

        if (event.type === EventType.Unavailable) {
          const resourceDetails = getSelectedResource(slots, event.resourceId);

          let startMoment = moment(start, DateFormats.YMDTime);

          if (!startMoment.isValid()) {
            startMoment = moment(
              resourceDetails?.slotInfo?.slotStart ||
                TIME_CONSTANTS.START_OF_DAY,
              DateFormats.YMDTime
            );
          }

          if (
            resourceDetails?.slotInfo?.slotStart &&
            moment(
              resourceDetails.slotInfo.slotStart || TIME_CONSTANTS.START_OF_DAY,
              DateFormats.YMDTime
            ).isAfter(startMoment)
          ) {
            startMoment = moment(
              resourceDetails.slotInfo.slotStart || TIME_CONSTANTS.START_OF_DAY,
              DateFormats.YMDTime
            );
          }

          startMoment = startMoment.add(1, DateFormats.Minutes);

          start = selectedDate + "T" + startMoment.format(DateFormats.Time);

          if (end === null && resourceDetails?.slotInfo?.slotEnd) {
            let endMoment = moment(
              resourceDetails.slotInfo.slotEnd,
              DateFormats.YMDTime
            );

            endMoment = endMoment.subtract(1, DateFormats.Minutes);

            end = selectedDate + "T" + endMoment.format(DateFormats.Time);
          }
        }

        return {
          ...event,
          bookingId: event?.id?.toString(),
          classNames:
            !event.showPopup && event.type !== EventType.Unavailable
              ? ["disabled"]
              : undefined,
          display:
            event.type === EventType.Unavailable
              ? EventBackgroundType.Background
              : EventBackgroundType.Block,
          end,
          id:
            event.type === EventType.Exception
              ? event?.id?.toString() + "-" + event?.resourceId?.toString()
              : event?.id?.toString(),
          start,
          title: applyLocalization(event.title),
        };
      });

    let restrictionEvents: BookingCalendarEvent[] = [];

    if (
      slots?.resources &&
      Array.isArray(slots.resources) &&
      slots.resources.length > 0
    ) {
      const newCalendarTime = calculateResourceData(slots.resources, events);
      restrictionEvents = createResourceRestrictions(
        slots.resources,
        {
          end: newCalendarTime?.end || TIME_CONSTANTS.END_OF_DAY,
          endTime: newCalendarTime.endTime,
          start: newCalendarTime?.start || TIME_CONSTANTS.START_OF_DAY,
        },
        events
      );
    }

    dispatch(
      bookingCalendarSlice.actions.setCalendarEvents([
        ...events,
        ...restrictionEvents,
      ])
    );
    setIsLoading(false);
  };

  const createResourceRestrictions = (
    resources: ResourceType[],
    calendarTime: { start: string; end: string; endTime: moment.Moment },
    existingEvents: BookingCalendarEvent[]
  ): BookingCalendarEvent[] => {
    if (!resources || !Array.isArray(resources)) return [];

    const slotTime = slots?.slotSplit ?? BOOKING_CONSTANTS.WIDTH_PER_SLOT;
    const currentDate = moment(selectedDate).format(DateFormats.YMD);

    const restrictionEvents: BookingCalendarEvent[] = [];

    const addRestriction = (event: BookingCalendarEvent) => {
      const eventStart = moment(event.start);
      const eventEnd = moment(event.end);

      const overlapsExisting = existingEvents.some(
        e =>
          e.resourceId === event.resourceId &&
          e.type === EventType.Unavailable &&
          eventStart.isBefore(e.end) &&
          eventEnd.isAfter(e.start)
      );

      if (overlapsExisting) {
        return;
      }

      restrictionEvents.push(event);
    };

    resources.forEach(resource => {
      if (!resource?.slotInfo) return;

      const { slotEnd, slots, slotStart } = resource.slotInfo;
      const availableSlots = slots?.availableSlots || [];

      const startOfDay = moment(
        `${currentDate}T${calendarTime.start || TIME_CONSTANTS.START_OF_DAY}`,
        DateFormats.YMDTime
      );

      if (
        slotStart &&
        startOfDay.isBefore(moment(slotStart, DateFormats.YMDTime))
      ) {
        addRestriction({
          classNames: ["fg-event-disabled"],
          display: EventBackgroundType.Background,
          end: slotStart,
          id: `fg-disabled-${resource.id}-before`,
          resourceId: resource.id,
          start: `${currentDate}T${startOfDay.format(DateFormats.Time)}`,
          title: t("Calendar.notAvailable"),
          type: EventType.Restriction,
        });
      }

      const slotEndMoment = moment(slotEnd);

      if (slotEnd && calendarTime.endTime?.isAfter(slotEndMoment)) {
        addRestriction({
          classNames: ["fg-event-disabled"],
          display: EventBackgroundType.Background,
          end: `${currentDate}T${calendarTime.endTime?.format(DateFormats.Time) || TIME_CONSTANTS.END_OF_DAY}`,
          id: `fg-disabled-${resource.id}-after`,
          resourceId: resource.id,
          start: slotEnd,
          title: t("Calendar.notAvailable"),
          type: EventType.Restriction,
        });
      }

      const sortedSlots = [...availableSlots].sort((a, b) =>
        moment(`${currentDate}T${a}`, DateFormats.YMDTime).diff(
          moment(`${currentDate}T${b}`, DateFormats.YMDTime)
        )
      );
      const available = sortedSlots.map(s =>
        moment(`${currentDate}T${s}`, DateFormats.YMDTime)
      );

      let lastEnd = startOfDay.clone();

      if (selectedDate === moment().format(DateFormats.YMD)) {
        const now = getNextTimeMark(moment(), slotTime);

        if (now.isAfter(lastEnd)) {
          addRestriction({
            classNames: ["fg-event-disabled"],
            display: EventBackgroundType.Background,
            end: now.format(DateFormats.YMDTime),
            id: `fg-unavailable-today-${resource.id}`,
            resourceId: resource.id,
            start: lastEnd.format(DateFormats.YMDTime),
            title: t("Calendar.notAvailable"),
            type: EventType.Unavailable,
          });

          lastEnd = now.clone();
        }
      }

      available.forEach(slotMoment => {
        if (slotMoment.isAfter(lastEnd)) {
          addRestriction({
            classNames: ["fg-event-disabled"],
            display: EventBackgroundType.Background,
            end: slotMoment.format(DateFormats.YMDTime),
            id: `fg-gap-${resource.id}-${lastEnd.format("HHmm")}`,
            resourceId: resource.id,
            start: lastEnd.format(DateFormats.YMDTime),
            title: t("Calendar.notAvailable"),
            type: EventType.Restriction,
          });
        }

        lastEnd = slotMoment.clone().add(slotTime, "minutes");
      });

      if (lastEnd.isBefore(calendarTime.endTime)) {
        addRestriction({
          classNames: ["fg-event-disabled"],
          display: EventBackgroundType.Background,
          end:
            calendarTime.endTime?.format(DateFormats.YMDTime) ||
            TIME_CONSTANTS.END_OF_DAY,
          id: `fg-gap-${resource.id}-end`,
          resourceId: resource.id,
          start: lastEnd.format(DateFormats.YMDTime),
          title: t("Calendar.notAvailable"),
          type: EventType.Restriction,
        });
      }
    });

    return mergeRestrictions(restrictionEvents);
  };

  const mergeRestrictions = (events: BookingCalendarEvent[]) => {
    const grouped = events.reduce<Record<string, BookingCalendarEvent[]>>(
      (acc, ev) => {
        acc[ev.resourceId] = acc[ev.resourceId] || [];
        acc[ev.resourceId].push(ev);
        return acc;
      },
      {}
    );

    const merged: BookingCalendarEvent[] = [];

    Object.values(grouped).forEach(group => {
      const sorted = group.sort(
        (a, b) => moment(a.start).valueOf() - moment(b.start).valueOf()
      );

      let current = sorted[0];

      for (let i = 1; i < sorted.length; i++) {
        const ev = sorted[i];

        if (moment(ev.start).isSameOrBefore(current.end)) {
          if (moment(ev.end).isAfter(current.end)) {
            current.end = ev.end;
          }
        } else {
          merged.push(current);
          current = ev;
        }
      }

      merged.push(current);
    });

    return merged;
  };

  const normalizeEvents = (events: BookingCalendarEvent[]) => {
    if (!events || !Array.isArray(events)) return [];

    return events.map(e => {
      if (e?.end?.includes(TIME_CONSTANTS.INVALID_END)) {
        const datePart = e.end.split("T")[0];
        const nextDay = new Date(datePart);
        nextDay.setDate(nextDay.getDate() + 1);
        const fixedEnd =
          nextDay.toISOString().split("T")[0] +
          "T" +
          TIME_CONSTANTS.START_OF_DAY;
        return { ...e, end: fixedEnd };
      }
      return e;
    });
  };

  const calculateResourceData = (
    resourcesData: ResourceType[],
    events: BookingCalendarEvent[]
  ): {
    end: string;
    start: string;
    endTime: moment.Moment;
  } => {
    if (
      (!resourcesData || resourcesData.length === 0) &&
      (!events || events.length === 0)
    ) {
      setCalendarTime({
        end: TIME_CONSTANTS.END_OF_DAY,
        start: TIME_CONSTANTS.START_OF_DAY,
      });
      setResources([]);
      return {
        end: TIME_CONSTANTS.END_OF_DAY,
        endTime: moment(TIME_CONSTANTS.END_OF_DAY, DateFormats.Time),
        start: TIME_CONSTANTS.START_OF_DAY,
      };
    }

    const starts = resourcesData
      .map(r => r?.slotInfo?.slotStart)
      .filter(Boolean)
      .map(t => moment(t, DateFormats.YMDTime));

    const ends = resourcesData
      .map(r => r?.slotInfo?.slotEnd)
      .filter(Boolean)
      .map(t => moment(t, DateFormats.YMDTime));

    const eventStart = events
      .filter(r => r?.start !== null)
      .map(event => moment(event?.start, DateFormats.YMDTime));

    const eventEnd = events
      .filter(e => e?.end !== null)
      .map(event => moment(event?.end, DateFormats.YMDTime));

    if (starts.length === 0) {
      const StartDayMoment = moment(
        `${selectedDate}T${TIME_CONSTANTS.START_OF_DAY}`,
        DateFormats.YMDTime
      );

      starts.push(StartDayMoment);
    }

    if (ends.length === 0) {
      const EndDayMoment = moment(
        `${selectedDate}T${TIME_CONSTANTS.END_OF_DAY}`,
        DateFormats.YMDTime
      );
      ends.push(EndDayMoment);
    }

    starts.push(...eventStart);
    ends.push(...eventEnd);

    const startTime = moment.min(starts);
    const endTime = moment.max(ends);

    const updateEndTime = endTime.clone();

    const normalizedStart = startTime?.isValid()
      ? startTime.format(DateFormats.Time)
      : TIME_CONSTANTS.START_OF_DAY;

    const normalizedEnd =
      updateEndTime &&
      updateEndTime.isValid() &&
      updateEndTime.format(DateFormats.Time) !== TIME_CONSTANTS.INVALID_END &&
      updateEndTime.format(DateFormats.Time) !== TIME_CONSTANTS.START_OF_DAY
        ? updateEndTime.format(DateFormats.Time)
        : TIME_CONSTANTS.END_OF_DAY;

    const newCalendarTime = {
      end: normalizedEnd,
      endTime,
      start: normalizedStart,
    };

    setCalendarTime(newCalendarTime);

    const resources = resourcesData
      .filter(r => r?.slotInfo)
      .map(r => {
        return {
          businessHours: [],
          id: r.id,
          slotEnd: normalizedEnd,
          slotStart: normalizedStart,
          title: applyLocalization(r.title),
        };
      });

    setResources(resources);

    return newCalendarTime;
  };

  const renderEventContent = (eventInfo: EventContentArg) => {
    const { event } = eventInfo;

    const hasType = !!event.extendedProps?.type;
    const hasTitle = !!event.title;

    const shouldShowTimeOnly =
      (!hasType && !hasTitle) ||
      event.extendedProps?.type === EventType.TempBooking;

    let displayText = "";

    if (shouldShowTimeOnly && event.start && event.end) {
      const startMoment = moment(event.start);
      const endMoment = moment(event.end);

      const startTime = startMoment.format(DateFormats.Time);
      const endTime = endMoment.format(DateFormats.Time);

      displayText = `${startTime} - ${endTime}`;
    }

    if (shouldShowTimeOnly) {
      return <div className="ml-1">{displayText}</div>;
    }

    return (
      <div className="mt-1 ml-1">
        {event.extendedProps?.type === EventType.Booking && (
          <div className="mb-1 ml-1">{t("Booking")}</div>
        )}

        <div className="ml-1">{event?.title}</div>
      </div>
    );
  };

  return {
    calendarTime,
    createResourceRestrictions,
    fetchCalendarData,
    fetchCalendarParticipant,
    isLoading,
    normalizeEvents,
    renderEventContent,
    resources,
    setIsLoading,
    updateCalendarEvents,
  };
};
